import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
  Chip,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import MapModal from "./mapAddress"

const AddressModal = ({ addresses = [], onSave }) => {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  const [newAddress, setNewAddress] = useState({
    address: "",
    postcode: "",
    phone: "",
    city: "",
    building: "",
    floor: "",
  })
  const [addressList, setAddressList] = useState(addresses)
  const [showForm, setShowForm] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)

  useEffect(() => {
    setAddressList(addresses)
  }, [addresses])

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    if (!modalOpen) {
      // Reset form when opening
      setShowForm(false)
      setEditingIndex(null)
      setNewAddress({
        address: "",
        postcode: "",
        phone: "",
        city: "",
        building: "",
        floor: "",
      })
    }
  }

  const toggleForm = () => {
    setShowForm(!showForm)
    if (showForm) {
      setEditingIndex(null)
      setNewAddress({
        address: "",
        postcode: "",
        phone: "",
        city: "",
        building: "",
        floor: "",
      })
    }
  }

  const handleAddAddress = () => {
    if (editingIndex !== null) {
      // Update existing address
      const updatedAddresses = [...addressList]
      updatedAddresses[editingIndex] = newAddress
      setAddressList(updatedAddresses)
    } else {
      // Add new address
      setAddressList([...addressList, newAddress])
    }
    setNewAddress({
      address: "",
      postcode: "",
      phone: "",
      city: "",
      building: "",
      floor: "",
    })
    setShowForm(false)
    setEditingIndex(null)
  }

  const handleEditAddress = (index) => {
    setNewAddress(addressList[index])
    setEditingIndex(index)
    setShowForm(true)
  }

  const handleDeleteAddress = (index) => {
    const updatedAddresses = addressList.filter((_, i) => i !== index)
    setAddressList(updatedAddresses)
  }

  const handleSave = () => {
    onSave(addressList)
    toggleModal()
  }

  const handleMapSelect = (selectedAddress) => {
    setNewAddress({
      ...newAddress,
      address: selectedAddress,
    })
    setShowForm(true)
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<LocationOnIcon />}
        onClick={toggleModal}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          borderColor: "#e5e7eb",
          color: "#374151",
          "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
        }}
      >
        {t("Extra Addresses")}
        {addressList.length > 0 && (
          <Chip
            size="small"
            label={addressList.length}
            sx={{ ml: 1, height: 18, fontSize: "0.7rem", bgcolor: "#3b82f6", color: "#fff" }}
          />
        )}
      </Button>

      <Dialog
        open={modalOpen}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocationOnIcon sx={{ color: "#3b82f6" }} />
            <Typography variant="h6" fontWeight={600}>
              {t("Delivery Addresses")}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Address List */}
          {addressList.length > 0 ? (
            <List sx={{ py: 0 }}>
              {addressList.map((addr, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      px: 3,
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500} color="#111827">
                          {addr.address || t("No address")}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          {addr.city && (
                            <Typography variant="caption" color="#6b7280">
                              {addr.city}
                            </Typography>
                          )}
                          {addr.postcode && (
                            <Typography variant="caption" color="#6b7280">
                              • {addr.postcode}
                            </Typography>
                          )}
                          {addr.phone && (
                            <Typography variant="caption" color="#6b7280">
                              • {addr.phone}
                            </Typography>
                          )}
                          {addr.building && (
                            <Typography variant="caption" color="#6b7280">
                              • {t("Building")}: {addr.building}
                            </Typography>
                          )}
                          {addr.floor && (
                            <Typography variant="caption" color="#6b7280">
                              • {t("Floor")}: {addr.floor}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handleEditAddress(index)}
                        sx={{ color: "#6b7280", "&:hover": { color: "#3b82f6" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAddress(index)}
                        sx={{ color: "#6b7280", "&:hover": { color: "#ef4444" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < addressList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <LocationOnIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
              <Typography variant="body2" color="#6b7280">
                {t("No extra addresses added yet")}
              </Typography>
            </Box>
          )}

          {/* Add/Edit Form Toggle */}
          <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={showForm ? "contained" : "outlined"}
                size="small"
                startIcon={showForm ? <ExpandLessIcon /> : <AddIcon />}
                onClick={toggleForm}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {showForm ? t("Cancel") : editingIndex !== null ? t("Edit Address") : t("Add New Address")}
              </Button>
              <MapModal
                onAddressSelect={handleMapSelect}
              />
            </Stack>
          </Box>

          {/* Address Form */}
          <Collapse in={showForm}>
            <Box sx={{ px: 3, py: 2, bgcolor: "#f9fafb" }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Address")}
                  value={newAddress.address}
                  onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                  placeholder={t("Enter street address")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("City")}
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                    placeholder={t("Enter city")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Postcode")}
                    value={newAddress.postcode}
                    onChange={(e) => setNewAddress({ ...newAddress, postcode: e.target.value })}
                    placeholder={t("Enter postcode")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Phone")}
                    value={newAddress.phone}
                    onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                    placeholder={t("Enter phone")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Building")}
                    value={newAddress.building}
                    onChange={(e) => setNewAddress({ ...newAddress, building: e.target.value })}
                    placeholder={t("Building name/number")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                  />
                  <TextField
                    sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                    size="small"
                    label={t("Floor")}
                    value={newAddress.floor}
                    onChange={(e) => setNewAddress({ ...newAddress, floor: e.target.value })}
                    placeholder={t("Floor")}
                  />
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!newAddress.address}
                  onClick={handleAddAddress}
                  sx={{ alignSelf: "flex-start", textTransform: "none", borderRadius: 2 }}
                >
                  {editingIndex !== null ? t("Update Address") : t("Add Address")}
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={toggleModal}
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("Save Changes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

AddressModal.propTypes = {
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      postcode: PropTypes.string,
      phone: PropTypes.string,
      building: PropTypes.string,
      floor: PropTypes.string,
    })
  ),
  onSave: PropTypes.func,
}

export default AddressModal
